/**
 * Routing and the page shell.
 *
 * ── How the two languages are routed ───────────────────────────────────────
 * Every page is declared once, in PAGES below, and mounted twice: at "/path"
 * for English and "/es/path" for Spanish. Adding a page means one line in that
 * array; adding a language means one more <Route> branch. Nothing else needs to
 * know a second language exists.
 *
 * The paths themselves stay in English under /es (/es/privacy, not
 * /es/privacidad): nobody searches for a privacy notice by its address. The
 * guides are the exception — they are found by what they are called, and
 * someone in Mexico searches "presupuesto quincenal" — so a page can name its
 * Spanish path (`es` below), and SPANISH_PATHS in src/shared/language.ts maps
 * each guide's two addresses onto each other for the switch, the server and
 * hreflang.
 */
/*
 * ── The site and the app ─────────────────────────────────────────────────────
 *
 * Two products on one origin. The marketing site owns `/` and `/es`; the PWA
 * owns everything under `/app`. They share nothing but the domain — different
 * chrome, different tokens, different state — and the split happens here, above
 * the site's own header and footer, so the app never renders inside them.
 *
 * One origin rather than two was the open question in the handoff, and this is
 * the resolution: a single service worker scope, one manifest, and a landing
 * page whose CTA is an ordinary link into the app rather than a redirect to
 * another domain.
 *
 * Note this differs from the handoff's manifest, which puts the app at `/`. It
 * cannot be: the same path cannot serve both a public marketing page that has
 * to be indexed and a private ledger that must not be. `/app` is the smallest
 * change that resolves it, and the manifest's start_url and scope follow it.
 */
import { Suspense, lazy, useEffect, type ReactElement } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import type { Locale } from '../shared/waitlist';

import { I18nProvider, localeFromPath, useI18n } from './i18n';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import NotFoundPage from './pages/NotFoundPage';

/**
 * The front page is loaded on demand too, which looks odd until you count who
 * pays for it: the app at /app shares this entry chunk, and an installed user
 * opening their ledger should not download a marketing page's hero simulation,
 * feature cards and FAQ to do it. The landing page's own cost is one extra
 * request against a warm connection, and the service worker caches both.
 *
 * Legal pages are lazy for the ordinary reason: long, rarely visited, almost
 * pure text.
 */
const HomePage = lazy(() => import('./pages/HomePage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const CookiesPage = lazy(() => import('./pages/CookiesPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const DeleteAccountPage = lazy(() => import('./pages/DeleteAccountPage'));
const GuidesPage = lazy(() => import('./pages/GuidesPage'));
const GuidePage = lazy(() => import('./pages/GuidePage'));

const PAGES: { path: string; es?: string; element: ReactElement }[] = [
  { path: '', element: <HomePage /> },
  { path: 'privacy', element: <PrivacyPage /> },
  { path: 'terms', element: <TermsPage /> },
  { path: 'cookies', element: <CookiesPage /> },
  { path: 'support', element: <SupportPage /> },
  { path: 'delete-account', element: <DeleteAccountPage /> },
  { path: 'guides', es: 'guias', element: <GuidesPage /> },
  { path: 'guides/:slug', es: 'guias/:slug', element: <GuidePage /> },
];

/**
 * The PWA. Loaded only when someone actually opens it, which keeps the whole app
 * — IndexedDB layer, sync, every screen — out of the landing page's bundle.
 */
const AppRoot = lazy(() => import('./app/AppRoot'));

export default function App() {
  const location = useLocation();
  const locale = localeFromPath(location.pathname);

  // The app is a different product with its own chrome. It is matched before the
  // site's shell so that the marketing header and footer never wrap it.
  if (location.pathname === '/app' || location.pathname.startsWith('/app/')) {
    return (
      <Suspense fallback={null}>
        <AppRoot />
      </Suspense>
    );
  }

  return (
    <I18nProvider locale={locale}>
      <DocumentLanguage locale={locale} />
      <ScrollToTop />
      <Shell />
    </I18nProvider>
  );
}

function Shell() {
  const { t } = useI18n();

  return (
    <>
      {/* The first tab stop on the page, for keyboard and screen-reader users. */}
      <a className="skip-link" href="#main">
        {t.nav.skipToContent}
      </a>

      <Header />

      <main id="main">
        <Suspense fallback={<PageLoading />}>
          <Routes>
            {PAGES.map((page) => (
              <Route key={`en-${page.path}`} path={`/${page.path}`} element={page.element} />
            ))}
            {PAGES.map((page) => (
              <Route
                key={`es-${page.path}`}
                path={`/es/${page.es ?? page.path}`}
                element={page.element}
              />
            ))}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>

      <Footer />
    </>
  );
}

/**
 * Keep <html lang> in step with the route.
 *
 * This is not cosmetic: screen readers choose a pronunciation from it, and
 * getting it wrong makes Spanish read aloud in an English accent. Browsers also
 * use it to offer translation, and search engines to confirm the page's
 * language.
 */
function DocumentLanguage({ locale }: { locale: Locale }) {
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
  return null;
}

/**
 * Reset the scroll position on navigation.
 *
 * A single-page app keeps the scroll offset when the route changes, so without
 * this, following a footer link from halfway down the front page lands you
 * halfway down the privacy policy. Anchor links (#faq) are left alone, since
 * for those the offset is the entire point.
 */
function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname, hash]);

  return null;
}

/** Shown while a lazily-loaded page is fetched. Sized to avoid a layout jump. */
function PageLoading() {
  return <div style={{ minHeight: '60vh' }} aria-hidden="true" />;
}
